import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

/** Header cell with a small inline-search popover that auto-hides 3s after blur. */
export default function ColumnSearch({ colId, label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const focusedRef = useRef(false);
  const hasFilter = value?.trim();

  const clearTimer = () => { if (timerRef.current) clearTimeout(timerRef.current); };
  const startTimer = () => {
    clearTimer();
    if (focusedRef.current) return;
    timerRef.current = setTimeout(() => setOpen(false), 3000);
  };

  useEffect(() => () => clearTimer(), []);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  return (
    <div onMouseEnter={clearTimer} onMouseLeave={() => { if (open) startTimer(); }}>
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <button
          onClick={(e) => { e.stopPropagation(); clearTimer(); setOpen((o) => !o); }}
          className={`p-0.5 rounded transition-colors ${hasFilter ? 'text-teal-500' : 'text-gray-400 hover:text-gray-600'}`}
          data-testid={`col-search-icon-${colId}`}
        >
          <Search className="w-3 h-3" />
        </button>
      </div>
      {open && (
        <div className="relative mt-1">
          <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={value || ''}
            onChange={(e) => onChange(colId, e.target.value)}
            onFocus={() => { focusedRef.current = true; clearTimer(); }}
            onBlur={() => { focusedRef.current = false; startTimer(); }}
            placeholder="..."
            className="w-full pl-5 pr-5 py-0.5 text-xs bg-white border border-gray-200 rounded focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-100"
            data-testid={`col-filter-${colId}`}
          />
          {hasFilter && (
            <button
              onClick={() => onChange(colId, '')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
              data-testid={`col-filter-clear-${colId}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
